import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import React, {
  useContext,
  createContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { auth } from './firebase';
import { db } from './firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import UserService from './services/user/UserService';
import TokenService from './services/token/TokenService';

interface AuthContextProps {
  googleSignIn: () => void;
  emailSignIn: (email: string, password: string) => void;
  logOut: () => void;
  user: User | null;
  tokenBalance: number;
  refreshTokenBalance: () => Promise<void>;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

const userService = new UserService();
const tokenService = new TokenService();

export const AuthContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokenBalance, setTokenBalance] = useState<number>(0);
  const navigate = useNavigate();

  const refreshTokenBalance = async () => {
    if (user) {
      const balance = await tokenService.getBalance(user.uid);
      setTokenBalance(balance);
    }
  };

  const googleSignIn = () => {
    const provider = new GoogleAuthProvider();
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    if (isMobile) {
      // Redirect flow: no popup/window.close, reliable on mobile browsers
      signInWithRedirect(auth, provider).catch((error) => {
        console.log(error);
      });
    } else {
      signInWithPopup(auth, provider)
        .then(() => {
          console.log('User signed in');
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const emailSignIn = (email: string, password: string) => {
    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;
        console.log(user);
        console.log('User signed in');
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const logOut = () => {
    signOut(auth)
      .then(() => {
        localStorage.removeItem('pokercity.expectSignIn');
        setTokenBalance(0);
        navigate('/');
        console.log('User signed out');
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    // Complete the mobile redirect sign-in when returning from Google
    getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          console.log('Redirect sign-in complete');
        }
      })
      .catch((error) => {
        console.log('Redirect result error:', error);
      });

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      localStorage.setItem('pokercity.expectSignIn', '1');

      if (currentUser) {
        // Set user data in Firestore
        await userService.setUserData(
          currentUser.uid,
          currentUser.displayName || '',
          currentUser.photoURL || '',
          currentUser.email || ''
        );

        // Award signup bonus if first time (TokenService checks internally)
        await tokenService.awardSignupBonus(currentUser.uid);

        // Load token balance with real-time listener
        const userRef = doc(db, 'users', currentUser.uid);
        const unsubBalance = onSnapshot(userRef, (snap) => {
          if (snap.exists()) {
            setTokenBalance(snap.data()?.token_balance ?? 0);
          }
        });
        return unsubBalance;
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ googleSignIn, emailSignIn, logOut, user, tokenBalance, refreshTokenBalance }}>
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('UserAuth must be used within a AuthContextProvider');
  }
  return context;
};
